import { useFormikContext } from "formik";
import React, { FC, useEffect, useState } from "react";
import { useLocale } from "@/contexts/localeContext";
import { TranslationKeys } from "@/contexts/TranslationKeys";
import { TableContextProvider } from "@/contexts/table/TableContextProvider";
import TableTab from "@/components/tabs/table-tab";
import PresetActionColumns from "@/models/ColumnDefinitions/PresetActionColumn";
import PresetActionSearch from "@/models/search/PresetActionSearch";
import { getAllLCDMessageActions } from "@/services/bioEventActionService";
import { getBioAddressProfileId } from "@/services/biometricService";
import { appendFilter } from "@/utilities/filters";
import settingTypesBasicFields from "@/constants/setting-types-basic-fields";
import { ListQuery } from "@/models/ListQuery";
import { ListData } from "@/models/ListData";
import { showError } from "@/services/utilities/ServiceError";
import { toast } from "react-toastify";

interface AssignStepProps {
    onNext: (e: any) => void;
    setStepDescriptions: React.Dispatch<React.SetStateAction<{ [key: string]: string }>>;
    bioAddressId: string;
}

const AssignStep: FC<AssignStepProps> = ({ onNext, setStepDescriptions, bioAddressId }) => {
    const { setFieldValue } = useFormikContext<any>();
    const { translate } = useLocale();
    const [profileId, setProfileId] = useState<string>("");

    useEffect(() => {
        getBioAddressProfileId(bioAddressId)
            .then((resolvedProfileId) => {
                setProfileId(resolvedProfileId ?? "");
            })
            .catch((error) => {
                setProfileId("");
                showError(error, toast.warn);
            });
    }, [bioAddressId]);

    const dataMethod = (listParameters?: ListQuery): Promise<ListData> => {
        if (!profileId) {
            return Promise.resolve({
                rows: [],
                pagination: { maxPageSize: 1, pageNumber: 1, totalCount: 0, totalPages: 0 }
            });
        }

        return getAllLCDMessageActions(translate, {
            ...listParameters,
            filters: appendFilter(
                settingTypesBasicFields.LCD.id,
                "Setting Type",
                "settingType.id",
                appendFilter(
                    profileId,
                    "Profile",
                    "profile.id",
                    listParameters?.filters || []
                )
            )
        });
    };

    const handleRowClick = (selectedItem: any) => {
        if (selectedItem) {
            setFieldValue("selectedAction", selectedItem);
            setStepDescriptions(prev => ({ ...prev, step1: selectedItem.nameOriginal }));
            onNext({});
        }
    };

    return <div className="pt-3 pr-3 h-full relative">
        <div className="overflow-y-auto" style={{ height: '87%' }}>
            <TableContextProvider
                key={profileId || "no-profile"}
                dataMethod={dataMethod}
                tableDescriptor={PresetActionColumns}
                searchDescriptor={PresetActionSearch}
                useTable
                usePagination
                useSearch
                useSort>
                <TableTab
                    title={translate.byKey(TranslationKeys.actions)}
                    selectRows={false}
                    onRowClicked={handleRowClick}
                    cyName="bioEventAssignStep" />
            </TableContextProvider>
        </div>
    </div>
};

export default AssignStep;